import { useEffect } from "react";

// Tooltips for the icon-only buttons in table cells (see "Buttons inside a table
// cell" in index.css, which shrinks them with the same selector). One listener for
// the whole console: on hover or keyboard focus it shows the button's label (its
// title, aria-label or text) in a small dark tip above it, drawn in <body> so the
// table's scroll box never clips it. A native title is parked while the tip shows,
// so the browser's own tooltip does not come up as well.
export const ICON_BUTTON = 'td button:has(> svg:first-child):not([role="combobox"]):not([role="checkbox"]):not([role="switch"]):not([data-label="keep"])';

const labelOf = (btn) =>
  (btn.dataset.tipTitle || btn.getAttribute("title") || btn.getAttribute("aria-label") || btn.textContent || "").replace(/\s+/g, " ").trim();

export function IconTips() {
  useEffect(() => {
    let supported = true;
    try { document.querySelector(ICON_BUTTON); } catch { supported = false; } // :has() missing: buttons keep their text
    if (!supported) return undefined;

    const tip = document.createElement("div");
    tip.className = "icon-tip";
    tip.setAttribute("role", "tooltip");
    tip.id = "icon-tip";
    document.body.appendChild(tip);
    let current = null;

    const hide = () => {
      if (!current) return;
      if (current.dataset.tipTitle !== undefined) { current.setAttribute("title", current.dataset.tipTitle); delete current.dataset.tipTitle; }
      current.removeAttribute("aria-describedby");
      current = null;
      tip.dataset.show = "false";
    };

    const show = (btn) => {
      if (btn === current) return;
      hide();
      const text = labelOf(btn);
      if (!text) return;
      current = btn;
      if (btn.hasAttribute("title")) { btn.dataset.tipTitle = btn.getAttribute("title"); btn.removeAttribute("title"); }
      btn.setAttribute("aria-describedby", "icon-tip");
      tip.textContent = text;
      tip.dataset.show = "false";
      tip.style.left = "0px"; tip.style.top = "0px";
      const r = btn.getBoundingClientRect();
      const t = tip.getBoundingClientRect();
      const left = Math.min(Math.max(8, r.left + r.width / 2 - t.width / 2), window.innerWidth - t.width - 8);
      const above = r.top - t.height - 8;
      tip.style.left = `${left}px`;
      tip.style.top = `${above >= 8 ? above : r.bottom + 8}px`;
      requestAnimationFrame(() => { if (current === btn) tip.dataset.show = "true"; });
    };

    const find = (el) => {
      const btn = el instanceof Element ? el.closest("td button") : null;
      return btn && btn.matches(ICON_BUTTON) ? btn : null;
    };
    const onOver = (e) => { const b = find(e.target); if (b) show(b); else if (current && !current.contains(e.target)) hide(); };
    const onFocus = (e) => { const b = find(e.target); if (b && b.matches(":focus-visible")) show(b); };
    const onOut = (e) => { if (current && !current.contains(e.relatedTarget)) hide(); };

    document.addEventListener("pointerover", onOver, true);
    document.addEventListener("focusin", onFocus, true);
    document.addEventListener("pointerout", onOut, true);
    document.addEventListener("focusout", hide, true);
    document.addEventListener("pointerdown", hide, true);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("blur", hide);
    return () => {
      hide();
      document.removeEventListener("pointerover", onOver, true);
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("pointerout", onOut, true);
      document.removeEventListener("focusout", hide, true);
      document.removeEventListener("pointerdown", hide, true);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("blur", hide);
      tip.remove();
    };
  }, []);
  return null;
}
